import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button } from "../../../../../components/ui";
import type { TrackingLink } from "../../../../../store/admin/trackingLinksStore";
import { TRACKING_LINK_TONES } from "../../../statusTones";
import { resolveStatus } from "../../../../admin/components/sections/trackingLinks/utils/helpers";
import { useRealTimeCountdown } from "../../../../admin/components/sections/trackingLinks/hooks/useRealTimeCountdown";

interface TrackingLinkCardProps {
  link: TrackingLink;
  viewMode: "grid" | "list";
  isToggling?: boolean;
  onCopy: (link: TrackingLink) => void;
  onDetails: (link: TrackingLink) => void;
  onEdit: (link: TrackingLink) => void;
  onToggleActive: (link: TrackingLink, activate: boolean) => void;
}

export default function TrackingLinkCard({
  link,
  viewMode,
  isToggling = false,
  onCopy,
  onDetails,
  onEdit,
  onToggleActive,
}: TrackingLinkCardProps) {
  const { t } = useTranslation("envios");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const status = resolveStatus(link.status);
  const isActive = status === "active";
  const isExpired = status === "expired";
  const countdown = useRealTimeCountdown(link.expirationDate, status);

  const busyLabel = isActive
    ? t("trackingLinks.busy.deactivating")
    : isExpired
      ? t("trackingLinks.busy.renewing")
      : t("trackingLinks.busy.activating");

  const idleLabel = isActive
    ? t("trackingLinks.actions.deactivate")
    : isExpired
      ? t("trackingLinks.actions.renew")
      : t("trackingLinks.actions.activate");

  const toggleLabel = isToggling ? busyLabel : idleLabel;

  const toggleIcon = isActive
    ? "ri-forbid-2-line"
    : isExpired
      ? "ri-refresh-line"
      : "ri-checkbox-circle-line";

  const statusLabel =
    status === "unknown"
      ? (link.status?.name ?? t("trackingLinks.status.unknown"))
      : t(`trackingLinks.status.${status}`);

  const accessLabel =
    link.accessType === "protected"
      ? t("trackingLinks.access.protected")
      : t("trackingLinks.access.public");

  const accessTone = link.accessType === "protected" ? "info" : "success";

  const accessIcon =
    link.accessType === "protected" ? "ri-lock-line" : "ri-global-line";

  const countdownClasses = countdown.isWarning
    ? "text-error font-bold font-mono text-xs"
    : "text-content font-mono text-xs";

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  if (viewMode === "list") {
    return (
      <div className="bg-card border border-line rounded-xl px-4 py-3.5 flex items-center gap-4 hover:border-line-soft transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-content truncate max-w-[200px]">
              {link.name}
            </span>
            <Badge tone={TRACKING_LINK_TONES[status]} size="sm">
              {statusLabel}
            </Badge>
            <Badge tone={accessTone} size="sm">
              <i className={`${accessIcon} mr-1 text-[9px]`} />
              {accessLabel}
            </Badge>
          </div>
          {link.internalNotes && (
            <p className="text-xs text-content-muted mt-0.5 truncate">
              {link.internalNotes}
            </p>
          )}
        </div>

        <div className="hidden md:flex items-center gap-6 text-xs shrink-0">
          <div className="text-center">
            <div className="text-[10px] text-content-muted mb-0.5">
              {t("trackingLinks.fields.remaining")}
            </div>
            <div className={countdownClasses}>{countdown.text}</div>
          </div>

          <div className="text-center">
            <div className="text-[10px] text-content-muted mb-0.5">
              {t("trackingLinks.fields.accessCount")}
            </div>
            <div className="text-xs font-semibold text-content">
              {link.accessCount > 0 ? link.accessCount : "—"}
            </div>
          </div>

          <div className="text-center">
            <div className="text-[10px] text-content-muted mb-0.5">
              {t("trackingLinks.fields.units")}
            </div>
            <div className="text-xs font-semibold text-content">
              {link.unitsCount}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" icon={<i className="ri-file-copy-line" />} onClick={() => onCopy(link)}>
            {t("trackingLinks.actions.copy")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-eye-line" />}
            onClick={() => onDetails(link)}
          >
            {t("trackingLinks.actions.details")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<i className="ri-pencil-line" />}
            onClick={() => onEdit(link)}
          >
            {t("trackingLinks.actions.edit")}
          </Button>
          <Button
            variant={isActive ? "dangerSoft" : "successSoft"}
            size="sm"
            loading={isToggling}
            icon={<i className={toggleIcon} />}
            onClick={() => onToggleActive(link, !isActive)}
          >
            {toggleLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-line rounded-2xl p-5 flex flex-col gap-4 hover:border-line-soft transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-content leading-snug break-words">
            {link.name}
          </h3>
          {link.internalNotes && (
            <p className="text-xs text-content-muted mt-0.5 line-clamp-1">
              {link.internalNotes}
            </p>
          )}
        </div>
        <div className="relative shrink-0" ref={menuRef}>
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            icon={<i className="ri-more-2-fill" />}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={t("trackingLinks.actions.details")}
          />
          {menuOpen && (
            <div className="absolute right-0 top-8 z-20 bg-card border border-line rounded-xl py-1.5 w-44 shadow-sm">
              <button
                onClick={() => {
                  onDetails(link);
                  setMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-content hover:bg-subtle transition-colors text-left whitespace-nowrap"
              >
                <i className="ri-eye-line text-xs text-content-muted" />
                {t("trackingLinks.actions.details")}
              </button>
              <button
                onClick={() => {
                  onCopy(link);
                  setMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-content hover:bg-subtle transition-colors text-left whitespace-nowrap"
              >
                <i className="ri-file-copy-line text-xs text-content-muted" />
                {t("trackingLinks.actions.copyUrl")}
              </button>
              <button
                onClick={() => {
                  window.open(link.publicUrl, "_blank", "noopener");
                  setMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-content hover:bg-subtle transition-colors text-left whitespace-nowrap"
              >
                <i className="ri-external-link-line text-xs text-content-muted" />
                {t("trackingLinks.actions.open")}
              </button>
              <button
                onClick={() => {
                  onEdit(link);
                  setMenuOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-content hover:bg-subtle transition-colors text-left whitespace-nowrap"
              >
                <i className="ri-pencil-line text-xs text-content-muted" />
                {t("trackingLinks.actions.edit")}
              </button>
              <button
                disabled={isToggling}
                onClick={() => {
                  onToggleActive(link, !isActive);
                  setMenuOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs transition-colors text-left whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? "text-error hover:bg-error/10"
                    : "text-primary-500 hover:bg-primary-500/10"
                }`}
              >
                <i
                  className={`${isToggling ? "ri-loader-4-line animate-spin" : toggleIcon} text-xs`}
                />
                {toggleLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Badge tone={TRACKING_LINK_TONES[status]}>{statusLabel}</Badge>
        <Badge tone={accessTone}>
          <i className={`${accessIcon} mr-1 text-xs`} />
          {accessLabel}
        </Badge>
      </div>

      <div className="flex flex-col gap-2.5 bg-app rounded-xl px-3.5 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-content-muted">
            {t("trackingLinks.fields.remaining")}
          </span>
          <span className={countdownClasses}>{countdown.text}</span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-content-muted">
            {t("trackingLinks.fields.accessCount")}
          </span>
          <span className="text-sm font-semibold text-content">
            {link.accessCount > 0 ? link.accessCount : "—"}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-content-muted">
            {t("trackingLinks.fields.units")}
          </span>
          <span className="text-sm font-semibold text-content">
            {link.unitsCount}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-auto">
        <Button size="sm" icon={<i className="ri-file-copy-line" />} onClick={() => onCopy(link)}>
          {t("trackingLinks.actions.copy")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          icon={<i className="ri-eye-line" />}
          onClick={() => onDetails(link)}
        >
          {t("trackingLinks.actions.details")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          icon={<i className="ri-pencil-line" />}
          onClick={() => onEdit(link)}
        >
          {t("trackingLinks.actions.edit")}
        </Button>
        <Button
          variant={isActive ? "dangerSoft" : "successSoft"}
          size="sm"
          loading={isToggling}
          icon={<i className={toggleIcon} />}
          onClick={() => onToggleActive(link, !isActive)}
        >
          {toggleLabel}
        </Button>
      </div>
    </div>
  );
}
